import { useEffect, useState } from "react";
import PageHeading from "../../components/PageElements/PageHeading";
import TableHeader from "../../components/Tables/TableHeader";
import Table from "../../components/Tables/Table";
import toast from "react-hot-toast";
import { Form, Modal, Space, Tag, Tooltip } from "antd";
import { Formik } from "formik";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import { MdLockReset } from "react-icons/md";
import { CiLock } from "react-icons/ci";
import { IoIosWarning } from "react-icons/io";
import FullScreenLoader from "../../components/Loaders/FullScreenLoader";
import TextInput from "../../components/Inputs/TextInput";
import PasswordInput from "../../components/Inputs/PasswordInput";
import FormCancelButton from "../../components/Buttons/FormCancelButton";
import FormButton from "../../components/Buttons/FormButton";
import DeleteModal from "../../components/Modals/DeleteModal";
import FormErrors from "../../components/Errors/FormErrors";

import {
  getSuperAdmins,
  createSuperAdmin,
  updateSuperAdmin,
  deleteSuperAdmin,
  activateSuperAdmin,
  deactivateSuperAdmin,
  resetAdminPassword, // ← NEW
} from "../../services/superAdminService";

import {
  createSuperAdminSchema,
  resetPasswordSchema,
  updateSuperAdminSchema,
} from "../../validators/superAdminValidators";

const SuperAdminList = () => {
  const [superAdmins, setSuperAdmins] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // NEW: password reset modal state
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [superAdminToResetPassword, setSuperAdminToResetPassword] =
    useState(null);

  const [errs, setErrs] = useState(null);
  const [superAdminToEdit, setSuperAdminToEdit] = useState(null);
  const [superAdminToDelete, setSuperAdminToDelete] = useState(null);
  const [modalText, setModalText] = useState("");

  useEffect(() => {
    loadSuperAdmins();
  }, []);

  useEffect(() => {
    let list = [...superAdmins];
    if (searchTerm.trim() !== "") {
      list = list.filter((u) =>
        `${u.username} ${u.first_name || ""} ${u.last_name || ""} ${
          u.email || ""
        }`
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      );
    }
    setFiltered(list);
  }, [searchTerm, superAdmins]);

  const loadSuperAdmins = async () => {
    setLoading(true);
    try {
      const { data } = await getSuperAdmins();
      setSuperAdmins(data.data ?? []);
    } catch (error) {
      toast.error(error?.response?.data?.error || "Failed to load UVCF Admins");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setIsDeleteModalOpen(false);
    setIsPasswordModalOpen(false);
    setSuperAdminToEdit(null);
    setSuperAdminToDelete(null);
    setSuperAdminToResetPassword(null);
    setErrs(null);
  };

  const handleEdit = (row) => {
    setSuperAdminToEdit(row);
    setIsModalOpen(true);
  };

  const handleDelete = (row) => {
    setSuperAdminToDelete(row);
    setModalText(
      `Please confirm that you want to delete %${row?.username?.toUpperCase()}`
    );
    setIsDeleteModalOpen(true);
  };

  // NEW: open reset password modal
  const handlePasswordReset = (row) => {
    setSuperAdminToResetPassword(row);
    setIsPasswordModalOpen(true);
  };

  const onCreate = async (payload, resetForm) => {
    setErrs(null);
    try {
      const { data } = await createSuperAdmin(payload);
      if (data?.data) setSuperAdmins((prev) => [...prev, data.data]);
      toast.success("UVCF Admin created successfully");
      setIsModalOpen(false);
      resetForm();
    } catch (error) {
      const { response } = error || {};
      if (response?.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(
          response?.data?.error || "UVCF Admin not created, try again!"
        );
      }
    }
  };

  const onUpdate = async (payload, resetForm) => {
    setErrs(null);
    try {
      const original = [...superAdmins];
      const idx = original.findIndex((u) => u.id === superAdminToEdit.id);
      if (idx === -1) throw new Error("UVCF Admin not found");

      const { data } = await updateSuperAdmin(superAdminToEdit.id, payload);
      original[idx] = data.data;
      setSuperAdmins(original);

      toast.success("UVCF Admin updated successfully!");
      setSuperAdminToEdit(null);
      setIsModalOpen(false);
      resetForm();
    } catch (error) {
      const { response } = error || {};
      if (response?.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(response?.data?.error || "Update failed, try again!");
      }
    }
  };

  const onSubmit = (payload, resetForm) => {
    if (superAdminToEdit) return onUpdate(payload, resetForm);
    return onCreate(payload, resetForm);
  };

  const onDeleteSuperAdmin = async () => {
    const original = [...superAdmins];
    const newList = original.filter((u) => u.id !== superAdminToDelete.id);
    setSuperAdmins(newList);

    try {
      await deleteSuperAdmin(superAdminToDelete.id);
      toast.success("UVCF Admin deleted successfully!");
    } catch (error) {
      const { response } = error || {};
      toast.error(
        response?.data?.message ||
          response?.data?.error ||
          "Delete failed, try again!"
      );
      setSuperAdmins(original);
    } finally {
      setIsDeleteModalOpen(false);
      setSuperAdminToDelete(null);
    }
  };

  const toggleActivation = async (row) => {
    const original = [...superAdmins];
    const idx = original.findIndex((u) => u.id === row.id);
    if (idx === -1) return;

    // optimistic flip
    original[idx] = { ...original[idx], is_active: !row.is_active };
    setSuperAdmins(original);

    try {
      if (row.is_active) {
        await deactivateSuperAdmin(row.id);
        toast.success("UVCF Admin deactivated");
      } else {
        await activateSuperAdmin(row.id);
        toast.success("UVCF Admin activated");
      }
    } catch (error) {
      // revert on failure
      original[idx] = row;
      setSuperAdmins(original);
      const { response } = error || {};
      toast.error(response?.data?.error || "Failed to toggle activation state");
    }
  };

  // NEW: submit handler for password reset modal
  const onSubmitReset = async (payload, resetForm) => {
    if (!superAdminToResetPassword) return;
    setErrs(null);
    try {
      await resetAdminPassword(superAdminToResetPassword.id, payload);
      toast.success("Admin Password reset successfully");
      setIsPasswordModalOpen(false);
      setSuperAdminToResetPassword(null);
      resetForm();
    } catch (error) {
      const { response } = error || {};
      if (response?.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(response?.data?.error || "Password not reset, try again!");
      }
    }
  };

  const columns = [
    { title: "#", render: (t, r, i) => i + 1, width: 70 },
    { title: "Username", dataIndex: "username", key: "username" },
    { title: "Email", dataIndex: "email", key: "email" },
    {
      title: "Role",
      render: (_, record) => <p>{record?.role?.name}</p>,
    },
    {
      title: "Status",
      render: (_, record) => (
        <Tag color={record.is_active ? "green" : "red"}>
          {record.is_active ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 260,
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Edit">
            <button
              className="p-2 border border-green-200 rounded-md text-green-600 hover:bg-green-50 transition"
              onClick={() => handleEdit(record)}
            >
              <FiEdit size={16} />
            </button>
          </Tooltip>

          <Tooltip title={record.is_active ? "Deactivate" : "Activate"}>
            <button
              className={`p-2 border rounded-md transition ${
                record.is_active
                  ? "border-yellow-200 text-yellow-700 hover:bg-yellow-50"
                  : "border-blue-200 text-blue-700 hover:bg-blue-50"
              }`}
              onClick={() => toggleActivation(record)}
            >
              {record.is_active ? "Deactivate" : "Activate"}
            </button>
          </Tooltip>

          {/* NEW: Reset Password */}
          <Tooltip title="Reset Password">
            <button
              onClick={() => handlePasswordReset(record)}
              className="p-2 border border-gray-200 rounded-md text-gray-700 hover:bg-gray-50 transition"
            >
              <MdLockReset size={16} />
            </button>
          </Tooltip>

          <Tooltip title="Delete">
            <button
              className="p-2 border border-red-200 rounded-md text-red-500 hover:bg-red-50 transition"
              onClick={() => handleDelete(record)}
            >
              <FiTrash2 size={16} />
            </button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  if (loading) return <FullScreenLoader />;

  return (
    <>
      <PageHeading title="UVCF Administrators" subtitle1="UVCF Admins" />

      <div className="px-8 py-6">
        <div className="bg-white shadow-sm rounded-lg p-6">
          <TableHeader
            title="UVCF Administrator List"
            count={superAdmins.length}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            buttonLabel="+ Add UVCF Admin"
            onButtonClick={() => setIsModalOpen(true)}
          />

          <Table
            isPaginable
            pageSize={10}
            isSearchable={false}
            loading={loading}
            columns={columns}
            rows={filtered.map((item) => ({ ...item, key: item.id }))}
          />

          {/* Create / Edit Modal */}
          <Modal
            title={
              <div className="px-4 py-3">
                <h2 className="text-lg font-semibold text-gray-700">
                  {superAdminToEdit ? "Edit UVCF Admin" : "Add UVCF Admin"}
                </h2>
              </div>
            }
            open={isModalOpen}
            onCancel={handleCancel}
            footer={false}
            width={900}
          >
            <Formik
              initialValues={{
                username: superAdminToEdit ? superAdminToEdit.username : "",
                email: superAdminToEdit ? superAdminToEdit.email : "",
                password: "",
                password_confirmation: "",
              }}
              enableReinitialize
              validationSchema={
                superAdminToEdit
                  ? updateSuperAdminSchema
                  : createSuperAdminSchema
              }
              onSubmit={(values, { resetForm }) => onSubmit(values, resetForm)}
            >
              {({
                values,
                handleChange,
                handleBlur,
                errors,
                submitForm,
                touched,
              }) => (
                <Form className="space-y-2 px-4! py-4!">
                  {errs && <FormErrors errors={errs} />}

                  <div className="grid gap-5 grid-cols-1 md:grid-cols-2">
                    <TextInput
                      name="username"
                      title="Username"
                      placeholder="E.g. superadmin"
                      requiredField={true}
                      errors={errors}
                      values={values}
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                    />
                    <TextInput
                      name="email"
                      title="Email"
                      placeholder="E.g. admin@renu.ac.ug"
                      requiredField={true}
                      errors={errors}
                      values={values}
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                    />
                  </div>

                  {!superAdminToEdit && (
                    <div className="grid gap-5 grid-cols-1 md:grid-cols-2">
                      <PasswordInput
                        name="password"
                        title="Password"
                        type="password"
                        placeholder="Enter password"
                        requiredField
                        icon={<CiLock size={16} />}
                        values={values}
                        handleBlur={handleBlur}
                        handleChange={handleChange}
                        errors={touched.password && errors.password}
                        touched={touched.password}
                      />
                      <PasswordInput
                        name="password_confirmation"
                        title="Confirm Password"
                        placeholder="Enter password"
                        type="password"
                        icon={<CiLock size={16} />}
                        requiredField={true}
                        errors={
                          touched.password_confirmation &&
                          errors.password_confirmation
                        }
                        values={values}
                        handleBlur={handleBlur}
                        handleChange={handleChange}
                      />
                    </div>
                  )}

                  <div className="flex justify-end gap-2">
                    <FormCancelButton text="Cancel" onClick={handleCancel} />
                    <FormButton
                      text="Save"
                      type="primary"
                      onClick={submitForm}
                    />
                  </div>
                </Form>
              )}
            </Formik>
          </Modal>

          {/* RESET PASSWORD MODAL — NEW */}
          <Modal
            title={
              <div className="px-4 py-3">
                <h2 className="text-lg font-semibold text-gray-700">
                  Reset Password
                </h2>
              </div>
            }
            open={isPasswordModalOpen}
            onCancel={handleCancel}
            width="50%"
            footer={false}
          >
            <Formik
              initialValues={{
                password: "",
                password_confirmation: "",
              }}
              enableReinitialize
              validationSchema={resetPasswordSchema}
              onSubmit={(values, { resetForm }) =>
                onSubmitReset(values, resetForm)
              }
            >
              {({
                values,
                handleChange,
                handleBlur,
                errors,
                submitForm,
                touched,
              }) => (
                <Form className="space-y-2 px-4! py-4!">
                  {errs && <FormErrors errors={errs} />}

                  <div className="grid gap-5 grid-cols-1 md:grid-cols-2">
                    <PasswordInput
                      name="password"
                      title="Password"
                      type="password"
                      placeholder="Enter password"
                      requiredField
                      icon={<CiLock size={16} />}
                      values={values}
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                      errors={touched.password && errors.password}
                      touched={touched.password}
                    />

                    <PasswordInput
                      name="password_confirmation"
                      title="Confirm Password"
                      placeholder="Enter password"
                      type="password"
                      icon={<CiLock size={16} />}
                      requiredField={true}
                      errors={
                        touched.password_confirmation &&
                        errors.password_confirmation
                      }
                      values={values}
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                    />
                  </div>

                  <div className="flex justify-end gap-2">
                    <FormCancelButton text="Cancel" onClick={handleCancel} />
                    <FormButton
                      text="Save"
                      type="primary"
                      onClick={submitForm}
                    />
                  </div>
                </Form>
              )}
            </Formik>
          </Modal>

          {/* Delete Modal */}
          <DeleteModal
            open={isDeleteModalOpen}
            handleOk={onDeleteSuperAdmin}
            handleCancel={handleCancel}
            modalText={modalText}
            extra={
              <div className="mt-3 bg-red-100 text-red-500 border-[1px] border-red-300 mx-5 px-3 py-2 rounded-md">
                <h3 className="text-red-700 text-md flex gap-1 items-center">
                  <IoIosWarning /> Warning
                </h3>
                Please note that this action cannot be reversed!
              </div>
            }
          />
        </div>
      </div>
    </>
  );
};

export default SuperAdminList;
